import { TolgeeProvider, useTolgee } from '@tolgee/react';
import { type ReactNode, useMemo } from 'react';
import { IntlProvider } from 'react-intl';
import csMessages from './messages/cs.json';
import enMessages from './messages/en.json';
import { tolgee } from './tolgee';

const messagesByLocale: Record<string, Record<string, string>> = {
  cs: csMessages,
  en: enMessages,
};

/**
 * Detects the best matching language from browser settings.
 * Falls back to 'cs' (Czech is the primary Zoobook language).
 */
function detectBrowserLanguage(): string {
  const supported = ['cs', 'en', 'sk', 'de', 'pl', 'hu'];
  const browserLangs = navigator.languages ?? [navigator.language];

  for (const lang of browserLangs) {
    const base = lang.split('-')[0].toLowerCase();
    if (supported.includes(base)) {
      return base;
    }
  }

  return 'cs';
}

/**
 * Inner component that bridges Tolgee language state to react-intl.
 * Must be rendered inside TolgeeProvider.
 */
function IntlBridge({ children }: { children: ReactNode }) {
  const tolgeeInstance = useTolgee(['language']);
  const language = tolgeeInstance.getLanguage() ?? 'cs';

  const messages = useMemo(() => messagesByLocale[language] ?? messagesByLocale.cs, [language]);

  return (
    <IntlProvider
      locale={language}
      defaultLocale="cs"
      messages={messages}
      onError={(err) => {
        // Suppress missing translation warnings in dev — Tolgee handles those
        if (err.code === 'MISSING_TRANSLATION') return;
        console.error(err);
      }}
    >
      {children}
    </IntlProvider>
  );
}

interface I18nProviderProps {
  children: ReactNode;
}

/**
 * Combined i18n provider: Tolgee (in-context editing + translation management)
 * wrapping react-intl (formatting + ICU message syntax).
 *
 * Usage: wrap your app root with <I18nProvider>.
 */
export function I18nProvider({ children }: I18nProviderProps) {
  // Set initial language from browser detection
  // (will be overridden by user_preferences.language when authenticated)
  const initialLanguage = detectBrowserLanguage();

  // Ensure tolgee starts with detected language
  if (tolgee.getLanguage() !== initialLanguage) {
    void tolgee.changeLanguage(initialLanguage);
  }

  return (
    <TolgeeProvider tolgee={tolgee} fallback="Loading...">
      <IntlBridge>{children}</IntlBridge>
    </TolgeeProvider>
  );
}
